import { AnimatePresence } from 'framer-motion'
import { useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { AssistantDrawer } from './AssistantDrawer'
import { CommandPalette } from './CommandPalette'
import { ErrorBoundary } from './ErrorBoundary'
import { HapiBuddy } from './HapiBuddy'
import { Header } from './Header'
import { MobileBottomNav, MobileNavDrawer } from './MobileNav'
import { MouseTrail } from './MouseTrail'
import { PageTransition } from './PageTransition'
import { Sidebar } from './Sidebar'
import { useUI } from '../context/UIContext'

export function AppShell() {
  const location = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  const { assistantOpen, setAssistantOpen } = useUI()

  // Page content scrolls inside <main>, not the window, so React Router's
  // route change leaves the incoming page sitting at the outgoing page's
  // scroll offset — tapping a client partway down a long list opened its
  // detail page already scrolled halfway down. Reset on every navigation.
  //
  // Keyed on pathname alone on purpose: a search-param change on the same
  // page (the Board clearing its ?highlight= after the pulse, say) must not
  // yank the view back to the top mid-interaction.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 })
    // Belt and braces for mobile, where the document itself can be scrolled
    // by the collapsing browser chrome even though the layout is h-screen.
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <div className="flex h-screen bg-cream-50">
      <MouseTrail />
      <Sidebar />
      <MobileNavDrawer />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header />
        {/* Brand aurora / light-leak background — lives here once, behind
         * every route's Outlet, rather than per-page, so it's consistent
         * app-wide. Theme-aware (see .app-canvas/.aurora-blob-* in
         * index.css): light base + soft pastel blobs by default, deeper
         * dark base + saturated blobs under Settings > Appearance > Theme
         * dark. Page content (PageHeader text, and any text that isn't
         * inside an opaque bg-white card) uses `--color-canvas-fg` /
         * `text-canvas-fg` so it flips with the canvas and stays legible
         * — see each page's `<PageHeader variant="dark">` and loose-text
         * overrides. */}
        <main ref={mainRef} className="app-canvas relative flex-1 overflow-y-auto scrollbar-thin pb-20 lg:pb-0">
          <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
            {/* Three-tone aurora — violet, brand pink, coral-red — the same
             * accent colors already used across the stat card icons, so the
             * ambient glow reads as "this app's palette" rather than a
             * one-off decoration. */}
            <div className="aurora-blob-1 absolute -left-24 -top-10 h-[55vh] w-[55vw]" />
            <div className="aurora-blob-2 absolute left-1/2 top-1/3 h-[42vh] w-[58vw] -translate-x-1/2 -translate-y-1/2" />
            <div className="aurora-blob-3 absolute -right-24 bottom-0 h-[55vh] w-[55vw]" />
            {/* Fluted-glass texture — faint vertical ribs, softened with a
             * blur so they read as light refraction rather than hard lines. */}
            <div
              className="absolute inset-0 opacity-[0.05] mix-blend-overlay blur-[1px]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, rgba(255,255,255,0.7) 0px, rgba(255,255,255,0.7) 1px, transparent 1px, transparent 8px)',
              }}
            />
          </div>
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <AnimatePresence mode="wait">
              <PageTransition key={location.pathname}>
                <ErrorBoundary resetKey={location.pathname}>
                  <Outlet />
                </ErrorBoundary>
              </PageTransition>
            </AnimatePresence>
          </div>
        </main>
      </div>
      <MobileBottomNav />
      <CommandPalette />
      <HapiBuddy />
      <AssistantDrawer open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </div>
  )
}
